import { useEffect, useRef, useState, type FormEvent } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { usePostHog } from "@posthog/react";
import { ArrowRight, Compass, Anchor, X } from "@phosphor-icons/react";
import { APPLICATION_URL, playCopy, principles, seaCopy } from "../../content";
import { BOAT_FLEET, boatStyleById, type BoatStyleId } from "../../game/boatFleet";
import { HarbourEngine, MAX_CREW, type Input, type LandmarkKind, type Nearby, type Snapshot } from "../../game/engine";
import { render } from "../../game/render";
import { FounderSignatures } from "../ui/FounderSignatures";
import { NoticeWallBoard } from "./NoticeWallBoard";

type Screen = "title" | "playing" | "dialog" | "arrived";

type Dialog =
  | { kind: "port"; id: string; label: string; line: string; reply: string }
  | { kind: "crew"; id: string }
  | { kind: "bottle"; id: string; text: string }
  | { kind: "lighthouse"; paragraphs: string[]; signoff: string; signoffNote: string }
  | { kind: "fleet"; name: string; blurb: string }
  | { kind: "landmark"; lk: LandmarkKind; name: string }
  | { kind: "rule"; index: number }
  | { kind: "whale" };

const STORAGE_KEY = "same-boat-harbour-v1";

function readSnapshot(e: HarbourEngine): Snapshot {
  return e.snapshot();
}

function isMissionReady(snapshot: Snapshot) {
  return Boolean(snapshot.vessel) && snapshot.crew.length > 0 && snapshot.rules >= 3;
}

function nextMissionObjective(snapshot: Snapshot) {
  if (!snapshot.vessel) return playCopy.mission.vessel;
  if (snapshot.crew.length === 0) return playCopy.mission.crew;
  if (snapshot.rules < 3) return `${playCopy.mission.rules} (${snapshot.rules}/3)`;
  return playCopy.mission.return;
}

export function HarbourGame({ onReadPage }: { onReadPage: () => void }) {
  const prefersReduced = useReducedMotion() ?? false;
  const posthog = usePostHog();
  const voyageCompletedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<HarbourEngine | null>(null);
  const inputRef = useRef<Input>({
    up: false,
    down: false,
    left: false,
    right: false,
    pointerActive: false,
    pointerScreen: { x: 0, y: 0 },
  });
  const screenRef = useRef<Screen>("title");
  const nearbyIdRef = useRef<string | null>(null);

  const [screen, setScreenState] = useState<Screen>("title");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [nearby, setNearby] = useState<Nearby>(null);
  const [hud, setHud] = useState<Snapshot>({
    vessel: null,
    crew: [],
    visited: 0,
    ports: 8,
    notesRead: 0,
    logRead: false,
    rules: 0,
    rulesTotal: 5,
    landmarks: 0,
    landmarksTotal: 3,
    whaleFound: false,
  });
  const [announce, setAnnounce] = useState("");
  const [crewName, setCrewName] = useState("");
  const [selectedBoat, setSelectedBoat] = useState<BoatStyleId | null>(null);
  const [storyCaption, setStoryCaption] = useState(playCopy.story.opening);
  const selectedBoatOption = boatStyleById(selectedBoat);

  const setScreen = (s: Screen) => {
    screenRef.current = s;
    setScreenState(s);
  };

  // ---- engine + loop setup (once) ----
  useEffect(() => {
    const engine = new HarbourEngine();
    engine.reducedMotion = prefersReduced;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) engine.restore(raw);
    } catch {
      /* ignore */
    }
    engineRef.current = engine;
    setSelectedBoat(engine.boatStyle);
    if (import.meta.env.DEV) (window as unknown as { __harbour?: HarbourEngine }).__harbour = engine;
    const restoredSnapshot = readSnapshot(engine);
    setHud(restoredSnapshot);
    if (restoredSnapshot.visited > 0 || restoredSnapshot.crew.length > 0) {
      setStoryCaption(playCopy.story.resumed);
    }

    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;

    let raf = 0;
    let last = performance.now();

    const resize = () => {
      const el = containerRef.current!;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = el.clientWidth;
      const h = el.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      engine.view = { w, h };
      dprRef.current = dpr;
    };
    const dprRef = { current: 1 };
    resize();
    window.addEventListener("resize", resize);

    const frame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 1 / 30);
      last = now;
      if (screenRef.current === "playing") {
        engine.update(dt, inputRef.current);
        const id = engine.nearby?.id ?? null;
        if (id !== nearbyIdRef.current) {
          nearbyIdRef.current = id;
          setNearby(engine.nearby);
        }
      } else {
        engine.idle(dt);
      }
      render(ctx, engine, { w: engine.view.w, h: engine.view.h, dpr: dprRef.current });
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- keyboard ----
  useEffect(() => {
    const isTextEntry = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      return target.isContentEditable || target.matches("input, textarea, select, [role='textbox']");
    };
    const setKey = (e: KeyboardEvent, down: boolean) => {
      const k = e.key.toLowerCase();
      const inp = inputRef.current;
      let handled = true;
      if (k === "arrowup" || k === "w") inp.up = down;
      else if (k === "arrowdown" || k === "s") inp.down = down;
      else if (k === "arrowleft" || k === "a") inp.left = down;
      else if (k === "arrowright" || k === "d") inp.right = down;
      else handled = false;
      if (handled) e.preventDefault();
    };
    const onDown = (e: KeyboardEvent) => {
      if (screenRef.current === "dialog" && e.key === "Escape") {
        closeDialog();
        return;
      }
      if (isTextEntry(e.target)) return;
      setKey(e, true);
      if ((e.key === "e" || e.key === "E" || e.key === " ") && !e.repeat && screenRef.current === "playing") {
        e.preventDefault();
        handleInteract();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (isTextEntry(e.target)) return;
      setKey(e, false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // release keys when leaving the water
  useEffect(() => {
    if (screen !== "playing") {
      const inp = inputRef.current;
      inp.up = inp.down = inp.left = inp.right = false;
      inp.pointerActive = false;
    }
  }, [screen]);

  const persist = () => {
    const e = engineRef.current;
    if (!e) return;
    try {
      localStorage.setItem(STORAGE_KEY, e.serialize());
    } catch {
      /* ignore */
    }
  };

  const pointerPos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    inputRef.current.pointerScreen = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (screenRef.current !== "playing") return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    pointerPos(e);
    inputRef.current.pointerActive = true;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (inputRef.current.pointerActive) pointerPos(e);
  };
  const onPointerUp = () => {
    inputRef.current.pointerActive = false;
  };

  const handleInteract = () => {
    const e = engineRef.current;
    if (!e || !e.nearby) return;
    const n = e.nearby;
    if (n.kind === "harbour") {
      const snapshot = readSnapshot(e);
      if (isMissionReady(snapshot)) {
        if (!voyageCompletedRef.current) {
          voyageCompletedRef.current = true;
          posthog?.capture("voyage_completed", {
            vessel: snapshot.vessel,
            crew_count: snapshot.crew.length,
            boats_visited: snapshot.visited,
            harbour_rules: snapshot.rules,
            whale_found: snapshot.whaleFound,
          });
        }
        setScreen("arrived");
      } else {
        const missing = nextMissionObjective(snapshot);
        setStoryCaption(playCopy.story.harbourLocked(missing));
        setAnnounce(`Mission incomplete. ${missing}`);
      }
      return;
    }
    if (n.kind === "port") {
      const p = e.portById(n.id)!;
      e.visitPort(p.id);
      persist();
      setHud(readSnapshot(e));
      setStoryCaption(playCopy.story.port(p.label));
      setDialog({ kind: "port", id: p.id, label: p.label, line: p.line, reply: p.reply });
      setScreen("dialog");
    } else if (n.kind === "crew") {
      if (e.crew.length >= MAX_CREW) {
        setAnnounce(playCopy.crew.full);
        return;
      }
      setCrewName("");
      setDialog({ kind: "crew", id: n.id });
      setScreen("dialog");
    } else if (n.kind === "bottle") {
      const b = e.bottleById(n.id)!;
      e.readBottle(b.id);
      persist();
      setHud(readSnapshot(e));
      setStoryCaption(playCopy.story.bottle);
      setDialog({ kind: "bottle", id: b.id, text: b.text });
      setScreen("dialog");
    } else if (n.kind === "lighthouse") {
      e.readLog();
      persist();
      setHud(readSnapshot(e));
      setStoryCaption(playCopy.story.lighthouse);
      const lh = e.world.lighthouse;
      setDialog({ kind: "lighthouse", paragraphs: lh.paragraphs, signoff: lh.signoff, signoffNote: lh.signoffNote });
      setScreen("dialog");
    } else if (n.kind === "fleet") {
      const f = e.fleetById(n.id)!;
      setStoryCaption(playCopy.story.fleet);
      setDialog({ kind: "fleet", name: f.name, blurb: f.blurb });
      setScreen("dialog");
    } else if (n.kind === "landmark") {
      const lm = e.landmarkById(n.id)!;
      e.seeLandmark(lm.id);
      persist();
      setHud(readSnapshot(e));
      setStoryCaption(playCopy.story.landmark(lm.name));
      setDialog({ kind: "landmark", lk: lm.kind, name: lm.name });
      setScreen("dialog");
    } else if (n.kind === "rule") {
      const rule = e.ruleById(n.id)!;
      const alreadyLogged = e.collectedRules.has(rule.id);
      e.collectRule(rule.id);
      persist();
      const snapshot = readSnapshot(e);
      setHud(snapshot);
      if (!alreadyLogged) {
        posthog?.capture("harbour_rule_collected", {
          rule_index: rule.index + 1,
          rules_collected: snapshot.rules,
        });
      }
      setAnnounce(`Harbour rule ${rule.index + 1} logged.`);
      setStoryCaption(isMissionReady(snapshot) ? playCopy.story.ready : playCopy.story.rule(rule.index + 1));
      setDialog({ kind: "rule", index: rule.index });
      setScreen("dialog");
    } else if (n.kind === "whale") {
      e.findWhale();
      persist();
      setHud(readSnapshot(e));
      setStoryCaption(playCopy.story.whale);
      setDialog({ kind: "whale" });
      setScreen("dialog");
    }
  };

  const closeDialog = () => {
    setDialog(null);
    setScreen("playing");
    nearbyIdRef.current = null; // allow re-prompt
  };

  const claimPort = (id: string, label: string) => {
    const e = engineRef.current!;
    e.claimPort(id);
    persist();
    const snapshot = readSnapshot(e);
    setHud(snapshot);
    posthog?.capture("boat_claimed", { boat: label, boats_visited: snapshot.visited });
    setAnnounce(`Claimed the ${label} boat.`);
    setStoryCaption(isMissionReady(snapshot) ? playCopy.story.ready : playCopy.story.claimed(label));
    closeDialog();
  };

  const submitCrew = (ev: FormEvent) => {
    ev.preventDefault();
    const e = engineRef.current!;
    if (dialog?.kind !== "crew") return;
    const name = crewName.trim();
    if (!name) return;
    e.rescueCrew(dialog.id, name);
    persist();
    const snapshot = readSnapshot(e);
    setHud(snapshot);
    posthog?.capture("crew_rescued", { crew_count: snapshot.crew.length });
    setAnnounce(`${name} joined your crew.`);
    setStoryCaption(isMissionReady(snapshot) ? playCopy.story.ready : playCopy.story.crew(name));
    closeDialog();
  };

  const resetVoyage = () => {
    const e = engineRef.current!;
    e.reset();
    persist();
    setHud(readSnapshot(e));
    nearbyIdRef.current = null;
    setNearby(null);
    setSelectedBoat(null);
    setStoryCaption(playCopy.story.opening);
    setScreen("title");
  };

  const chooseBoat = (id: BoatStyleId) => {
    setSelectedBoat(id);
    if (engineRef.current) engineRef.current.boatStyle = id;
  };

  const startVoyage = () => {
    const e = engineRef.current;
    if (!e || !selectedBoat) return;
    e.boatStyle = selectedBoat;
    persist();
    const resumed = hud.visited > 0 || hud.crew.length > 0;
    posthog?.capture("voyage_started", { boat_style: selectedBoat, resumed });
    setStoryCaption(resumed ? playCopy.story.resumed : playCopy.story.opening);
    setScreen("playing");
  };

  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const missionReady = isMissionReady(hud);

  return (
    <div ref={containerRef} className="fixed inset-0 overflow-hidden bg-paper select-none">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute inset-0 h-full w-full touch-none"
        role="application"
        aria-label="Same Boat harbour. Sail your boat with the arrow keys, visit the boats, and make port to apply."
      />

      <p aria-live="polite" className="sr-only">
        {announce}
      </p>

      {/* HUD (visible while sailing) */}
      {(screen === "playing" || screen === "dialog") && (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-3 md:p-5">
            <div className="pointer-events-auto flex flex-col items-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-2 rounded-full border-2 border-ink bg-foam/90 px-3 py-1.5 font-mono text-xs text-ink backdrop-blur-sm">
                  <Anchor weight="bold" className="size-4 text-signal" aria-hidden />
                  {hud.visited}/{hud.ports} {playCopy.hud.ports}
                </span>
                <span className="flex items-center gap-2 rounded-full border-2 border-ink bg-foam/90 px-3 py-1.5 font-mono text-xs text-ink backdrop-blur-sm">
                  {playCopy.hud.crew}: {hud.crew.length}
                </span>
                {hud.rules > 0 && (
                  <span className="hidden items-center gap-2 rounded-full border-2 border-ink bg-foam/90 px-3 py-1.5 font-mono text-xs text-ink backdrop-blur-sm md:flex">
                    {seaCopy.rules.tally}: {hud.rules}/{hud.rulesTotal}
                  </span>
                )}
                {hud.vessel && (
                  <span className="hidden items-center gap-2 rounded-full border-2 border-ink bg-ink px-3 py-1.5 font-mono text-xs text-foam sm:flex">
                    {playCopy.hud.vessel}: {hud.vessel}
                  </span>
                )}
              </div>
              <div className="max-w-[min(26rem,72vw)] rounded-md border border-ink bg-foam/90 px-3 py-2 backdrop-blur-sm">
                <p className="font-mono text-[9px] tracking-[0.16em] text-signal-deep uppercase">
                  {missionReady ? playCopy.mission.ready : playCopy.mission.label}
                </p>
                <p className="mt-0.5 text-xs font-semibold text-ink">{nextMissionObjective(hud)}</p>
              </div>
            </div>
            <div className="pointer-events-auto flex shrink-0 items-center gap-2">
              <button
                onClick={onReadPage}
                className="hidden rounded-full border-2 border-ink bg-foam/95 px-3 py-1.5 font-mono text-[11px] whitespace-nowrap text-ink shadow-card transition-colors hover:bg-foam md:block"
              >
                {playCopy.readPage}
              </button>
              <a
                href={APPLICATION_URL}
                {...(APPLICATION_URL.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                className="rounded-full bg-signal px-4 py-1.5 text-sm font-semibold whitespace-nowrap text-ink shadow-card transition-colors hover:bg-signal-deep hover:text-foam"
              >
                {playCopy.hud.apply}
              </a>
            </div>
          </div>

          {/* controls hint (bottom-left) */}
          <p className="pointer-events-none absolute bottom-3 left-3 max-w-[15rem] rounded-md border border-line bg-foam/80 px-3 py-2 font-mono text-[11px] leading-relaxed text-muted backdrop-blur-sm md:bottom-5 md:left-5">
            {isTouch ? playCopy.controlsTouch : playCopy.controlsDesktop}
          </p>

          {/* voyage captions + contextual action, stacked so they can never overlap */}
          <div className="pointer-events-none absolute inset-x-3 bottom-20 flex flex-col items-center gap-3 md:inset-x-72 md:bottom-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={storyCaption}
                initial={prefersReduced ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                transition={{ duration: 0.28 }}
                role="status"
                aria-live="polite"
                className="max-w-2xl rounded-md border border-foam/30 bg-ink/90 px-4 py-3 text-center text-foam shadow-lift backdrop-blur-sm md:px-6"
              >
                <p className="font-mono text-[9px] tracking-[0.2em] text-signal uppercase">{playCopy.story.label}</p>
                <p className="mt-1 text-sm leading-snug md:text-[15px]">{storyCaption}</p>
              </motion.div>
            </AnimatePresence>
            <AnimatePresence>
              {nearby && (
                <motion.button
                  key={nearby.kind + nearby.id}
                  initial={prefersReduced ? false : { opacity: 0, y: 12, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleInteract}
                  className="pointer-events-auto flex items-center gap-2 rounded-full border-2 border-ink bg-ink px-6 py-3 text-base font-semibold text-foam shadow-lift"
                >
                  {nearby.kind === "harbour" && !missionReady ? playCopy.mission.lockedAction : playCopy.action[nearby.kind]}
                  <ArrowRight weight="bold" className="size-4" aria-hidden />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Title screen */}
      <AnimatePresence>
        {screen === "title" && (
          <motion.div
            initial={prefersReduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 flex items-center justify-center overflow-y-auto bg-paper/70 p-3 backdrop-blur-[2px] sm:p-5"
          >
            <div className="my-auto w-full max-w-6xl rounded-md border-2 border-ink bg-foam p-5 shadow-lift sm:p-7 md:p-9">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-signal-deep uppercase">Before you leave the dock · 01/01</p>
                  <h1 className="display-type-wide mt-2 text-[clamp(2.4rem,7vw,5rem)] leading-none text-ink">Choose your boat.</h1>
                </div>
                <p className="max-w-md text-sm leading-relaxed text-ink-soft md:text-right">
                  Seven hulls. Same water. Your choice changes what you sail, not where you belong.
                </p>
              </div>

              <div
                role="radiogroup"
                aria-label="Choose one of seven boats"
                className="mt-6 grid auto-cols-[8.5rem] grid-flow-col gap-2 overflow-x-auto pb-3 sm:auto-cols-[9.5rem] lg:grid-flow-row lg:grid-cols-7 lg:overflow-visible lg:pb-0"
              >
                {BOAT_FLEET.map((boat, index) => {
                  const isSelected = selectedBoat === boat.id;
                  return (
                    <button
                      key={boat.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => chooseBoat(boat.id)}
                      className={`group relative min-h-52 overflow-hidden border-2 px-3 pt-3 pb-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-1 motion-reduce:hover:translate-y-0 ${
                        isSelected ? "border-ink bg-paper-deep" : "border-line bg-paper hover:border-ink"
                      }`}
                    >
                      <span className="absolute top-2 left-2 font-mono text-[9px] tracking-[0.12em] text-muted">{String(index + 1).padStart(2, "0")}</span>
                      <div className="flex h-32 items-center justify-center">
                        <img
                          src={boat.src}
                          alt=""
                          aria-hidden
                          draggable={false}
                          className={`h-[7.5rem] w-auto object-contain transition-transform duration-300 ${isSelected ? "scale-105" : "group-hover:scale-105"}`}
                        />
                      </div>
                      <span className="block text-sm font-bold text-ink">{boat.name}</span>
                      <span className="mt-1 block font-mono text-[9px] leading-snug text-muted">
                        {isSelected ? "ready at the dock" : "choose this hull"}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-col justify-between gap-4 border-t border-line pt-5 sm:flex-row sm:items-center">
                <div className="min-h-12 max-w-xl">
                  <p className="font-mono text-[10px] tracking-[0.15em] text-muted uppercase">
                    {selectedBoatOption ? `Selected · ${selectedBoatOption.name}` : "Pick a hull to begin"}
                  </p>
                  <p className="mt-1 text-sm text-ink-soft">
                    {selectedBoatOption?.detail ?? "There are no stats to optimise. Choose the one you want to see crossing the harbour."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={startVoyage}
                  disabled={!selectedBoat}
                  className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-signal px-7 text-base font-semibold text-ink transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:bg-signal-deep hover:text-foam disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0"
                >
                  {hud.visited > 0 || hud.crew.length > 0 ? playCopy.resume : "Take the helm"}
                  <Compass weight="bold" className="size-5" aria-hidden />
                </button>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                <p className="font-mono text-[10px] leading-relaxed text-muted">{playCopy.objective}</p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <button onClick={onReadPage} className="font-mono text-xs text-muted underline underline-offset-4 hover:text-ink">
                  {playCopy.readPage}
                </button>
                <a
                  href={APPLICATION_URL}
                  {...(APPLICATION_URL.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                  className="font-mono text-xs text-muted underline underline-offset-4 hover:text-ink"
                >
                  {playCopy.skip}
                </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dialog panels */}
      <AnimatePresence>
        {/* The Notice Wall gets its own full-screen board instead of a panel. */}
        {screen === "dialog" && dialog?.kind === "landmark" && dialog.lk === "noticewall" && (
          <NoticeWallBoard key="notice-wall" onClose={closeDialog} />
        )}
        {screen === "dialog" && dialog && !(dialog.kind === "landmark" && dialog.lk === "noticewall") && (
          <motion.div
            key="dialog-panel"
            initial={prefersReduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-end justify-center bg-ink/20 p-4 md:items-center"
            onClick={closeDialog}
          >
            <motion.div
              initial={prefersReduced ? false : { opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md p-7 shadow-lift md:p-8 ${dialog.kind === "lighthouse" ? "parchment-letter max-h-[88dvh] overflow-y-auto border-0" : "rounded-md border-2 border-ink bg-foam"}`}
            >
              <div className="mb-4 flex items-start justify-between gap-4">
                <span className="font-mono text-[11px] tracking-widest text-muted uppercase">
                  {dialog.kind === "port" && "the boats"}
                  {dialog.kind === "crew" && "the muster"}
                  {dialog.kind === "bottle" && playCopy.bottle.label}
                  {dialog.kind === "lighthouse" && playCopy.log.label}
                  {dialog.kind === "fleet" && seaCopy.fleet.label}
                  {dialog.kind === "landmark" && dialog.name}
                  {dialog.kind === "rule" && seaCopy.rules.label}
                  {dialog.kind === "whale" && seaCopy.whale.label}
                </span>
                <button
                  onClick={closeDialog}
                  aria-label="Back to the water"
                  className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-ink text-ink transition-colors hover:bg-paper-deep"
                >
                  <X weight="bold" className="size-4" aria-hidden />
                </button>
              </div>

              {dialog.kind === "port" && (
                <PortDialog dialog={dialog} hud={hud} onClaim={claimPort} onClose={closeDialog} />
              )}

              {dialog.kind === "crew" && (
                <form onSubmit={submitCrew} noValidate>
                  <h2 className="display-type text-[clamp(1.6rem,3.5vw,2.4rem)] text-ink">{playCopy.crew.title}</h2>
                  <label htmlFor="crew-name" className="mt-4 block text-sm font-semibold text-ink">
                    {playCopy.crew.prompt}
                  </label>
                  <input
                    id="crew-name"
                    autoFocus
                    value={crewName}
                    maxLength={24}
                    onChange={(e) => setCrewName(e.target.value)}
                    placeholder={playCopy.crew.placeholder}
                    className="mt-2 h-12 w-full rounded-md border-2 border-ink bg-paper px-4 text-ink placeholder:text-muted"
                  />
                  <p className="mt-2 font-mono text-xs text-muted">{playCopy.crew.note}</p>
                  <button
                    type="submit"
                    disabled={!crewName.trim()}
                    className="mt-5 inline-flex h-12 items-center gap-2 rounded-full bg-ink px-6 text-[0.95rem] font-semibold text-foam transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0"
                  >
                    {playCopy.crew.add}
                    <ArrowRight weight="bold" className="size-4" aria-hidden />
                  </button>
                </form>
              )}

              {dialog.kind === "bottle" && (
                <>
                  <p className="rotate-[-0.6deg] rounded-md bg-manila p-6 text-xl leading-snug font-medium text-ink md:text-2xl">
                    {dialog.text}
                  </p>
                  <p className="mt-4 font-mono text-xs text-muted">{playCopy.bottle.from}</p>
                  <button
                    onClick={closeDialog}
                    className="mt-5 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
                  >
                    {playCopy.resume}
                  </button>
                </>
              )}

              {dialog.kind === "lighthouse" && (
                <>
                  <div className="space-y-4 text-ink-soft">
                    {dialog.paragraphs.map((para) => (
                      <p key={para.slice(0, 24)} className="leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                  <FounderSignatures className="mt-7" />
                  <p className="font-mono text-xs text-muted">{dialog.signoffNote}</p>
                  <button
                    onClick={closeDialog}
                    className="mt-5 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
                  >
                    {playCopy.resume}
                  </button>
                </>
              )}

              {dialog.kind === "fleet" && (
                <>
                  <h2 className="display-type text-[clamp(1.7rem,3.8vw,2.5rem)] text-ink">{dialog.name}</h2>
                  <p className="mt-3 text-lg text-ink-soft">{dialog.blurb}</p>
                  <p className="mt-4 font-mono text-sm text-signal-deep">{seaCopy.fleet.ask}</p>
                  <button
                    onClick={closeDialog}
                    className="mt-5 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
                  >
                    {seaCopy.fleet.close}
                  </button>
                </>
              )}

              {dialog.kind === "landmark" && (
                <LandmarkDialog lk={dialog.lk} onClose={closeDialog} />
              )}

              {dialog.kind === "rule" && (
                <>
                  <p className="font-mono text-xs text-muted">
                    {seaCopy.rules.label} {dialog.index + 1} {seaCopy.rules.of} {hud.rulesTotal}
                  </p>
                  <h2 className="mt-2 display-type text-[clamp(1.7rem,3.8vw,2.6rem)] text-ink">
                    {principles[dialog.index]?.title}
                  </h2>
                  <p className="mt-3 text-lg text-ink-soft">{principles[dialog.index]?.line}</p>
                  <p className="mt-5 font-mono text-xs text-muted">
                    {hud.rules}/{hud.rulesTotal} {seaCopy.rules.tally}
                  </p>
                  <button
                    onClick={closeDialog}
                    className="mt-4 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
                  >
                    {playCopy.resume}
                  </button>
                </>
              )}

              {dialog.kind === "whale" && (
                <>
                  <h2 className="display-type text-[clamp(1.7rem,3.8vw,2.5rem)] text-ink">{seaCopy.whale.title}</h2>
                  <div className="mt-3 space-y-3 text-ink-soft">
                    {seaCopy.whale.lines.map((l) => (
                      <p key={l.slice(0, 16)} className="leading-relaxed">
                        {l}
                      </p>
                    ))}
                  </div>
                  <button
                    onClick={closeDialog}
                    className="mt-5 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
                  >
                    {seaCopy.whale.close}
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Arrival */}
      <AnimatePresence>
        {screen === "arrived" && (
          <motion.div
            initial={prefersReduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 flex items-center justify-center bg-paper/80 p-4 backdrop-blur-sm"
          >
            <motion.div
              initial={prefersReduced ? false : { opacity: 0, y: 26, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="flex w-full max-w-[46rem] flex-col overflow-hidden rounded-md border-2 border-ink bg-foam shadow-lift sm:flex-row"
            >
              <div className="min-w-0 flex-1 p-8 md:p-10">
                <h2 className="display-type-wide text-[clamp(2rem,5vw,3.4rem)] text-ink">{playCopy.arrived.title}</h2>
                <p className="mt-3 text-ink-soft">{playCopy.arrived.sub}</p>
                <p className="mt-5 font-mono text-sm text-muted">
                  {hud.visited === hud.ports
                    ? playCopy.arrived.full
                    : hud.visited > 0
                      ? playCopy.arrived.some
                      : playCopy.arrived.none}
                </p>
                <p className="mt-4 text-ink-soft">{playCopy.arrived.closing}</p>
                <a
                  href={APPLICATION_URL}
                  {...(APPLICATION_URL.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-signal px-8 py-3.5 text-base font-semibold text-ink transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:bg-signal-deep hover:text-foam motion-reduce:hover:translate-y-0"
                >
                  {playCopy.arrived.apply}
                  <ArrowRight weight="bold" className="size-5" aria-hidden />
                </a>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                  <button
                    onClick={() => setScreen("playing")}
                    className="font-mono text-xs text-muted underline underline-offset-4 hover:text-ink"
                  >
                    {playCopy.arrived.keepSailing}
                  </button>
                  <button onClick={onReadPage} className="font-mono text-xs text-muted underline underline-offset-4 hover:text-ink">
                    {playCopy.readPage}
                  </button>
                  <button onClick={resetVoyage} className="font-mono text-xs text-muted underline underline-offset-4 hover:text-ink">
                    {playCopy.reset}
                  </button>
                </div>
              </div>

              {/* manifest stub */}
              <div className="w-full shrink-0 border-t-2 border-dashed border-ink p-8 sm:w-56 sm:border-t-0 sm:border-l-2">
                <dl className="space-y-4 font-mono text-xs text-ink">
                  <div>
                    <dt className="text-muted uppercase">{playCopy.arrived.vessel}</dt>
                    <dd className="mt-0.5 font-semibold">{hud.vessel ?? playCopy.hud.none}</dd>
                  </div>
                  <div>
                    <dt className="text-muted uppercase">{playCopy.arrived.crew}</dt>
                    <dd className="mt-0.5 font-semibold">
                      {hud.crew.length > 0 ? `you + ${hud.crew.length}` : playCopy.arrived.solo}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted uppercase">{playCopy.arrived.boatsSeen}</dt>
                    <dd className="mt-0.5 font-semibold">
                      {hud.visited}/{hud.ports}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted uppercase">{seaCopy.rules.tally}</dt>
                    <dd className="mt-0.5 font-semibold">
                      {hud.rules}/{hud.rulesTotal}
                    </dd>
                  </div>
                </dl>
                {hud.whaleFound && <p className="mt-4 font-mono text-[11px] text-muted">you met the whale.</p>}
                <p className="mt-4 font-hand text-lg text-ink-soft">{playCopy.arrived.note}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LandmarkDialog({ lk, onClose }: { lk: LandmarkKind; onClose: () => void }) {
  const c = seaCopy.landmarks;
  return (
    <>
      {lk === "table" && (
        <>
          <h2 className="display-type text-[clamp(1.7rem,3.6vw,2.5rem)] text-ink">
            {c.table.titleLines.map((l) => (
              <span key={l} className="block">
                {l}
              </span>
            ))}
          </h2>
          <p className="mt-3 text-ink-soft">{c.table.sub}</p>
          <ul className="mt-4 space-y-1.5">
            {c.table.prompts.map((p) => (
              <li key={p} className="font-hand text-xl text-ink">
                {p}
              </li>
            ))}
          </ul>
          <p className="mt-4 font-mono text-xs text-muted">{c.table.note}</p>
        </>
      )}
      {lk === "workshop" && (
        <>
          <h2 className="display-type text-[clamp(1.8rem,4vw,2.6rem)] text-ink">{c.workshop.title}</h2>
          <p className="mt-3 text-lg text-ink-soft">{c.workshop.sub}</p>
          <p className="mt-4 rotate-[-1deg] font-hand text-xl text-ink-soft">{c.workshop.note}</p>
        </>
      )}
      {/* "noticewall" never reaches this dialog: it opens the full NoticeWallBoard. */}
      <button
        onClick={onClose}
        className="mt-5 inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
      >
        {playCopy.resume}
      </button>
    </>
  );
}

function PortDialog({
  dialog,
  hud,
  onClaim,
  onClose,
}: {
  dialog: { id: string; label: string; line: string; reply: string };
  hud: Snapshot;
  onClaim: (id: string, label: string) => void;
  onClose: () => void;
}) {
  const alreadyClaimedOther = hud.vessel !== null && hud.vessel !== dialog.label;
  const isThisOne = hud.vessel === dialog.label;
  return (
    <>
      <h2 className="display-type text-[clamp(1.8rem,4vw,2.8rem)] text-ink">{dialog.label}</h2>
      <p className="mt-3 text-lg text-ink-soft">{dialog.line}</p>
      <p className="mt-4 font-mono text-sm text-signal-deep">{dialog.reply}</p>
      {isThisOne ? (
        <p className="mt-5 font-hand text-2xl text-ink">{playCopy.port.claimed}</p>
      ) : (
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => onClaim(dialog.id, dialog.label)}
            className="inline-flex h-12 items-center gap-2 rounded-full bg-ink px-6 text-[0.95rem] font-semibold text-foam transition-transform duration-200 ease-out hover:-translate-y-0.5 motion-reduce:hover:translate-y-0"
          >
            {alreadyClaimedOther ? playCopy.port.switch : playCopy.port.claim}
            <ArrowRight weight="bold" className="size-4" aria-hidden />
          </button>
          <button
            onClick={onClose}
            className="inline-flex h-12 items-center rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold text-ink transition-colors hover:bg-paper-deep"
          >
            {playCopy.port.visiting}
          </button>
        </div>
      )}
    </>
  );
}
